import { DotsHorizontalIcon } from '@radix-ui/react-icons'
import { Row } from '@tanstack/react-table'
import type { UpdateTask } from 'api/generated'
import { Button } from 'components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import {
  Download,
  Info,
  MailX,
  Play,
  PlayCircle,
  RotateCw,
  Trash2,
} from 'lucide-react'
import { useTasks } from '../context/tasks-context'

interface DataTableRowActionsProps {
  row: Row<UpdateTask>
}

export function DataTableRowActions({ row }: DataTableRowActionsProps) {
  const task = row.original
  const { setOpen, setCurrentRow } = useTasks()

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          className='flex h-8 w-8 p-0 data-[state=open]:bg-muted'
        >
          <DotsHorizontalIcon className='h-4 w-4' />
          <span className='sr-only'>Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-[200px]'>
        {task?.status === 'pending' ? (
          <>
            <DropdownMenuItem
              onClick={() => {
                setCurrentRow(task)
                setOpen('run')
              }}
            >
              Esegui
              <DropdownMenuShortcut>
                <PlayCircle size={16} />
              </DropdownMenuShortcut>
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                setCurrentRow(task)
                setOpen('run-no-mail')
              }}
            >
              Esegui senza inviare email
              <DropdownMenuShortcut>
                <Play size={16} />
              </DropdownMenuShortcut>
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                setCurrentRow(task)
                setOpen('delete')
              }}
            >
              Cancella
              <DropdownMenuShortcut>
                <Trash2 size={16} />
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          </>
        ) : (
          <>
            {task.rerunnable && (
              <>
                <DropdownMenuItem
                  onClick={() => {
                    setCurrentRow(task)
                    setOpen('rerun')
                  }}
                >
                  Rilancia
                  <DropdownMenuShortcut>
                    <RotateCw size={16} />
                  </DropdownMenuShortcut>
                </DropdownMenuItem>
                {/* avoid_send_mail only applies to import files */}
                {!task.job_class && (
                  <DropdownMenuItem
                    onClick={() => {
                      setCurrentRow(task)
                      setOpen('rerun-no-mail')
                    }}
                  >
                    Rilancia senza inviare email
                    <DropdownMenuShortcut>
                      <MailX size={16} />
                    </DropdownMenuShortcut>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              onClick={() => {
                setCurrentRow(task)
                setOpen('delete')
              }}
            >
              Cancella
              <DropdownMenuShortcut>
                <Trash2 size={16} />
              </DropdownMenuShortcut>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setCurrentRow(task)
            setOpen('details')
          }}
        >
          Dettagli e file
          <DropdownMenuShortcut>
            <Info size={16} />
          </DropdownMenuShortcut>
        </DropdownMenuItem>
        {task.file_url && (
          <DropdownMenuItem asChild>
            <a
              href={task.file_url}
              target='_blank'
              rel='noreferrer'
              title={task.file ?? undefined}
            >
              Scarica file
              <DropdownMenuShortcut>
                <Download size={16} />
              </DropdownMenuShortcut>
            </a>
          </DropdownMenuItem>
        )}
        {/* Parameter files: presigned links valid 1 hour, null without S3 */}
        {task.parameter_files.map(({ key, url }) => (
          <DropdownMenuItem key={key} disabled={!url} asChild={!!url}>
            {url ? (
              <a href={url} target='_blank' rel='noreferrer' title={key}>
                <span className='truncate'>{key}</span>
                <DropdownMenuShortcut>
                  <Download size={16} />
                </DropdownMenuShortcut>
              </a>
            ) : (
              <span className='truncate' title={key}>
                {key}
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
